import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProductCard from '../components/store/ProductCard';
import CartDrawer from '../components/store/CartDrawer';
import FilterSidebar from '../components/store/FilterSidebar';
import { useCart } from '../contexts/CartContext';
import './Loja.css';
import API_BASE_URL from '../config/api';

const ATHLETE_RESTRICTED_SLUGS = ['kits', 'equipamentos'];

export default function Loja() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

  const [athleteSports, setAthleteSports] = useState(null);
  const isLoggedIn = !!localStorage.getItem('token');

  const { totalItems, setIsDrawerOpen } = useCart();

  const filters = {
    categoryId: searchParams.get('categoria') ? Number(searchParams.get('categoria')) : undefined,
    sportId: searchParams.get('modalidade') ? Number(searchParams.get('modalidade')) : undefined,
    search: searchParams.get('q') || undefined,
    page: Number(searchParams.get('page') || 1),
  };

  const updateFilter = useCallback((key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    newParams.delete('page');
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const clearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  useEffect(() => {
    if (!isLoggedIn) {
      setAthleteSports([]);
      return;
    }
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}/api/products/athlete-sports`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => setAthleteSports(Array.isArray(data) ? data : []))
      .catch(() => setAthleteSports([]));
  }, [isLoggedIn]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    Promise.all([
      fetch(`${API_BASE_URL}/api/products/categories`).then(r => r.json()),
      fetch(`${API_BASE_URL}/api/sports`).then(r => r.json()),
      fetch(`${API_BASE_URL}/api/products/featured?limit=8`, { headers }).then(r => r.json()),
    ]).then(([cats, spts, featured]) => {
      setCategories(Array.isArray(cats) ? cats : []);
      setSports(Array.isArray(spts) ? spts.filter(s => s.isActive) : []);
      setFeaturedProducts(Array.isArray(featured) ? featured : []);
    }).catch(console.error);
  }, []);

  const selectedCategory = categories.find(c => c.id === filters.categoryId);
  const isAthleteRestrictedSelected = ATHLETE_RESTRICTED_SLUGS.includes(selectedCategory?.slug);

  const hasAthleteAccess = isLoggedIn && athleteSports !== null && athleteSports.length > 0;

  useEffect(() => {
    if (athleteSports !== null && isAthleteRestrictedSelected && !hasAthleteAccess) {
      clearFilters();
    }
  }, [athleteSports, isAthleteRestrictedSelected, hasAthleteAccess, clearFilters]);

  const visibleCategories = categories.filter(cat => {
    if (ATHLETE_RESTRICTED_SLUGS.includes(cat.slug)) return hasAthleteAccess;
    return true;
  });
  const allowedSportIds = isAthleteRestrictedSelected && athleteSports
    ? athleteSports.map(s => s.sportId)
    : null;

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.categoryId) params.set('CategoryId', filters.categoryId);
    if (filters.sportId) params.set('SportId', filters.sportId);
    if (filters.size) params.set('Size', filters.size);
    if (filters.search) params.set('Search', filters.search);
    params.set('Page', filters.page);
    params.set('PageSize', '12');

    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    fetch(`${API_BASE_URL}/api/products?${params}`, { headers })
      .then(r => r.json())
      .then(data => {
        setProducts(data.items || []);
        setTotalCount(data.totalCount || 0);
        setCurrentPage(data.page || 1);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [searchParams, isAthleteRestrictedSelected, allowedSportIds]);

  const hasActiveFilters = filters.categoryId || filters.sportId || filters.size || filters.search;

  const activeCategory = visibleCategories.find(c => c.id === filters.categoryId);
  const isShopPage = hasActiveFilters || Number(searchParams.get('page') || 0) > 0;

  return (
    <div className="loja-page">
      <CartDrawer />

      {!isShopPage && (
        <section className="loja-hero" style={{ '--hero-logo-url': "url('/CDP_logo.png')" }}>
          <div className="loja-hero-content">
            <div className="loja-hero-text">
              <img src="/CDP_logo.png" alt="Logo CDP" className="loja-hero-logo-mobile" />
              <h1 className="loja-hero-title">Veste as<br />Cores do Clube</h1>
              <p className="loja-hero-subtitle">
                Equipamentos oficiais, kits de treino e merchandising exclusivo.
              </p>
            </div>
          </div>
        </section>
      )}
      {!isShopPage && featuredProducts.length > 0 && (
        <section className="loja-featured section-padding bg-light">
          <div className="container">
            <div className="section-header">
              <h2>Destaques da Loja</h2>
              <p>Os artigos mais populares do clube</p>
            </div>
            <div className="loja-products-grid">
              {featuredProducts.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
            <div className="loja-featured-cta">
              <button className="btn btn-outline-dark" onClick={() => updateFilter('q', ' ')}>
                Ver Todos os Produtos
              </button>
            </div>
          </div>
        </section>
      )}

      <section className="loja-shop section-padding">
        <div className="container">
          <div className="loja-shop-toolbar">
            <div className="loja-shop-toolbar-left">
              <h2 className="loja-shop-title">
                {filters.categoryId
                  ? activeCategory?.name || 'Produtos'
                  : 'Todos os Produtos'}
              </h2>
              {totalCount > 0 && (
                <span className="loja-shop-count">{totalCount} artigos</span>
              )}
            </div>
            <button
              className="loja-filter-toggle"
              onClick={() => setIsMobileFiltersOpen(true)}
              aria-label="Abrir filtros"
            >
              <i className="fas fa-sliders-h" /> Filtros
              {hasActiveFilters && <span className="filter-dot" />}
            </button>
          </div>

          {isAthleteRestrictedSelected && hasAthleteAccess && (
            <div className="loja-athlete-banner">
              <i className="fas fa-shield-alt" />
              <span>
                A mostrar {selectedCategory?.name?.toLowerCase() || 'artigos'} para:{' '}
                <strong>{athleteSports.map(s => s.sportName).join(', ')}</strong>
              </span>
            </div>
          )}

          <div className="loja-shop-layout">
            <FilterSidebar
              categories={visibleCategories}
              sports={sports}
              filters={filters}
              onFilterChange={updateFilter}
              onClearFilters={clearFilters}
              isMobileOpen={isMobileFiltersOpen}
              onMobileClose={() => setIsMobileFiltersOpen(false)}
              allowedSportIds={allowedSportIds}
            />

            <div className="loja-shop-main">
              <div className="loja-search-bar">
                <i className="fas fa-search" />
                <input
                  type="search"
                  placeholder="Pesquisar produtos..."
                  defaultValue={filters.search}
                  onChange={e => updateFilter('q', e.target.value || null)}
                  className="loja-search-input"
                  aria-label="Pesquisar produtos"
                />
              </div>

              {loading ? (
                <div className="loja-loading">
                  <div className="loja-spinner" />
                  <p>A carregar produtos...</p>
                </div>
              ) : products.length === 0 ? (
                <div className="loja-empty">
                  <i className="fas fa-search" />
                  <p>Nenhum produto encontrado</p>
                  {hasActiveFilters && (
                    <button className="btn btn-outline-dark" onClick={clearFilters}>
                      Limpar Filtros
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="loja-products-grid">
                    {products.map(p => <ProductCard key={p.id} product={p} />)}
                  </div>

                  {totalCount > 12 && (
                    <div className="loja-pagination">
                      <button
                        className="loja-page-btn"
                        disabled={currentPage === 1}
                        onClick={() => updateFilter('page', currentPage - 1)}
                      >
                        <i className="fas fa-chevron-left" />
                      </button>
                      <span className="loja-page-info">
                        Página {currentPage} de {Math.ceil(totalCount / 12)}
                      </span>
                      <button
                        className="loja-page-btn"
                        disabled={currentPage * 12 >= totalCount}
                        onClick={() => updateFilter('page', currentPage + 1)}
                      >
                        <i className="fas fa-chevron-right" />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {totalItems > 0 && (
        <button
          className="loja-cart-fab"
          onClick={() => setIsDrawerOpen(true)}
          aria-label={`Carrinho: ${totalItems} itens`}
        >
          <i className="fas fa-shopping-bag" />
          <span>Carrinho</span>
          <span className="loja-cart-fab-count">{totalItems}</span>
        </button>
      )}
    </div>
  );
}
